import { cloneDeep, debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DesignStateService from "../../../service/DesignStateService";
import TechpackService from "../../../service/TechpackService";
import { BEHAVES_LIKE } from "../../../constant/common.constant";
import { inchesToCm } from "../../../utils/LocaleUtil";
import { AssetColor, ProductDefinitionDecoration } from "../../../types";
import { TechpackItemArtwork } from "../../../types";
import {
  Box,
  FlexColBox,
  ListItem,
  Input,
  Icon,
} from "../../../common/components";
import { Typography } from "@mui/material";
import { palette } from "../../../styles/theme";
import ArtworkCustomColor from "./ArtworkCustomColor";
import ColorBoxPicker from "../../../common/components/Select/ColorSelect/ColorBox/ColorBoxPicker";

const formatNumber = (num: number | string | undefined): string => {
  if (!num) return "0";
  if (typeof num === "string") {
    num = parseFloat(num.split(" ")[0]);
  }
  const str = num.toString();
  if (str.includes(".")) {
    return str; // Keep original decimal places if number has decimals
  }
  return `${str}.0`; // Add .0 for whole numbers
};

interface Props {
  decoration: TechpackItemArtwork["decoration"];
  decorationDefinition?: ProductDefinitionDecoration;
  productionDecoration: TechpackItemArtwork["productionDecoration"];
  techpackId: string;
  techpackItemId: string;
  techpackItemArtwork?: TechpackItemArtwork;
}

const TechpackArtworkDescription = (props: Props) => {
  const {
    decoration,
    decorationDefinition,
    productionDecoration,
    techpackId,
    techpackItemId,
    techpackItemArtwork,
  } = props;

  const { t } = useTranslation();

  const [backgroundColorDef, setBackgroundColorDef] =
    useState<AssetColor | null>(null);
  const [stitchesColorDef, setStitchesColorDef] = useState<AssetColor | null>(
    null
  );
  const [patternBackgroundColorDef, setPatternBackgroundColorDef] =
    useState<AssetColor | null>(null);
  const [productionCalibrationColors, setProductionCalibrationColors] =
    useState<AssetColor[]>([
      {
        name: "",
        hex: "",
        label: "",
        techpackColorName: "",
        chartName: "",
        disabled: false,
      },
      {
        name: "",
        hex: "",
        label: "",
        techpackColorName: "",
        chartName: "",
        disabled: false,
      },
    ]);

  const tagDetails = useMemo(() => {
    try {
      // To keep legacy design working
      if (decoration.embroideryPatchDetails) {
        decoration.tagDetails = decoration.embroideryPatchDetails;
      }
      const tagName = decoration?.tagDetails?.tagName;
      const selectedTagSize = decorationDefinition?.tagOptions?.find(
        (t) => t.name === tagName
      );

      const decorationMaxWidth =
        (selectedTagSize?.widthInInches || 0) -
        2 * (selectedTagSize?.borderX || 0);
      const decorationMaxHeight =
        (selectedTagSize?.heightInInches || 0) -
        2 * (selectedTagSize?.borderY || 0);

      const decorationBoxRatio = decorationMaxWidth / decorationMaxHeight;
      const assetRatio = decoration.ratio;
      let assetWidth = "";
      let assetHeight = "";
      if (decorationBoxRatio < Number(assetRatio)) {
        // Width is limiting
        assetWidth = inchesToCm(decorationMaxWidth);
        assetHeight = inchesToCm(decorationMaxWidth / Number(assetRatio));
      } else {
        // Height is limiting
        assetWidth = inchesToCm(decorationMaxHeight * Number(assetRatio));
        assetHeight = inchesToCm(decorationMaxHeight);
      }

      if (decoration.tagDetails?.backgroundFabricName) {
        if (!decoration.tagDetails?.backgroundColorName?.startsWith("#")) {
          DesignStateService.getFabricColors(
            decoration.tagDetails?.backgroundFabricName
          ).then((colors) => {
            const bgColorDef = colors?.find(
              (c: AssetColor) =>
                c.name === decoration.tagDetails?.backgroundColorName
            );
            // Set background color
            setBackgroundColorDef(bgColorDef || null);
          });
        } else {
          // This is a custom color
          setBackgroundColorDef({
            hex: decoration.tagDetails?.backgroundColorName,
            chartName: "Custom",
            name: "Custom",
            label: decoration.tagDetails?.backgroundColorName,
            techpackColorName: "",
            disabled: false,
          });
        }
      }

      const planeAngle = decoration.location?.selectedLocation?.planeAngle;
      if ((planeAngle / 90) % 2) {
        // Flip width and height because of the plane angle
        return {
          assetWidth,
          assetHeight,
          tagWidth: inchesToCm(
            (selectedTagSize?.heightInInches || 0) +
              2 * (selectedTagSize?.stitchThicknessInches || 0)
          ),
          tagHeight: inchesToCm(
            (selectedTagSize?.widthInInches || 0) +
              2 * (selectedTagSize?.stitchThicknessInches || 0)
          ),
          planeAngle,
          moldCode: selectedTagSize?.moldCode,
        };
      }

      if (
        decorationDefinition?.behavesLike === BEHAVES_LIKE.EMBROIDERY_PATCH_TYPE
      ) {
        if (!decoration.tagDetails?.stitchColorName?.startsWith("#")) {
          if (decoration.tagDetails?.stitchFabricName) {
            DesignStateService.getFabricColors(
              decoration.tagDetails?.stitchFabricName
            ).then((colors) => {
              const stitchDef = colors?.find(
                (c: AssetColor) =>
                  c.name === decoration.tagDetails?.stitchColorName
              );
              // Set stitches color
              setStitchesColorDef(stitchDef || null);
            });
          } else {
            // Older designs might not have stitchFabricName set. In that case, get the fabric via
            // the asset manager. The stitches are the same color as the embroidery.
            const stitchDef = decoration.fabricColorCodes?.find(
              (c) => c.name === decoration.tagDetails?.stitchColorName
            );
            setStitchesColorDef(stitchDef || null);
          }
        } else {
          // This is a custom color
          setStitchesColorDef({
            hex: decoration.tagDetails?.stitchColorName,
            chartName: "Custom",
            name: "Custom",
            label: decoration.tagDetails?.stitchColorName,
            techpackColorName: "",
            disabled: false,
          });
        }
      }

      return {
        assetWidth,
        assetHeight,
        tagWidth: inchesToCm(
          (selectedTagSize?.widthInInches || 0) +
            2 * (selectedTagSize?.stitchThicknessInches || 0)
        ),
        tagHeight: inchesToCm(
          (selectedTagSize?.heightInInches || 0) +
            2 * (selectedTagSize?.stitchThicknessInches || 0)
        ),
        planeAngle,
        moldCode: selectedTagSize?.moldCode,
      };
    } catch (e) {
      return undefined; // If not a tag, the function will fail and it's ok.
    }
  }, [decorationDefinition, decoration]);

  const repeatingPatternLogoSize = useMemo(() => {
    try {
      const maxImageWidthInches =
        decoration?.repeatingDetails?.maxImageWidthInches;
      const maxImageHeightInches =
        decoration?.repeatingDetails?.maxImageHeightInches;
      const ratio = parseFloat(decoration.ratio);
      if (ratio > 1) {
        return {
          width: maxImageWidthInches,
          height: maxImageHeightInches / ratio,
        };
      }
      return {
        width: maxImageWidthInches * ratio,
        height: maxImageHeightInches,
      };
    } catch (e) {
      return undefined; // If not a tag, the function will fail and it's ok.
    }
  }, [decoration]);

  useEffect(() => {
    const fabricName = decorationDefinition?.generator?.fabricName;
    const backgroundColorName =
      decoration?.repeatingDetails?.backgroundColorName;
    if (!fabricName || !backgroundColorName) {
      setPatternBackgroundColorDef(null);
      return;
    }
    if (backgroundColorName.startsWith("#")) {
      // This is a custom color
      setPatternBackgroundColorDef({
        hex: backgroundColorName,
        chartName: "Custom",
        name: "Custom",
        label: backgroundColorName,
        techpackColorName: "",
        disabled: false,
      });
      return;
    }
    DesignStateService.getFabricColors(fabricName).then(
      (colors: AssetColor[]) => {
        const colorDef = colors?.find(
          (c: AssetColor) => c.name === backgroundColorName
        );
        setPatternBackgroundColorDef(colorDef ?? null);
      }
    );
  }, [
    decorationDefinition?.generator?.fabricName,
    decoration?.repeatingDetails?.backgroundColorName,
  ]);

  //Initial load (for detailed prints)
  useEffect(() => {
    const defaultCalibrationColors = [
      { name: "", hex: "" },
      { name: "", hex: "" },
      { name: "", hex: "" },
      { name: "", hex: "" },
    ];
    if (productionDecoration && productionDecoration?.calibrationColors) {
      productionDecoration.calibrationColors.forEach(
        (calibrationColor: string | AssetColor, index: number) => {
          //For now we limit only to 4 colors
          if (index < 4) {
            if (typeof calibrationColor === "string") {
              // Change the format for an object
              defaultCalibrationColors[index].name = calibrationColor as string;
            } else {
              defaultCalibrationColors[index] = calibrationColor as AssetColor;
            }
          }
        }
      );
    }
    setProductionCalibrationColors(defaultCalibrationColors as AssetColor[]);
  }, [productionDecoration, productionDecoration?.calibrationColors]);

  const saveCalibrationColors = debounce(async (calibrationColors) => {
    const productionArtwork = {
      techpackId,
      techpackItemId,
      techpackItemArtworkId: techpackItemArtwork?.id,
      calibrationColors,
    };

    return await TechpackService.updateProductionArtworkCalibrationColors(
      productionArtwork
    );
  });

  /**
   *
   */
  const onCalibrationColorChange = useCallback(
    async (index: number, key: keyof AssetColor, value: string) => {
      const newCalibrationColors = cloneDeep(productionCalibrationColors);
      newCalibrationColors[index][key] = value as never;
      setProductionCalibrationColors(newCalibrationColors);
      await saveCalibrationColors(newCalibrationColors);
    },
    [
      productionCalibrationColors,
      setProductionCalibrationColors,
      saveCalibrationColors,
    ]
  );

  const clearColorSelection = useCallback(
    async (index: number) => {
      const newCalibrationColors = cloneDeep(productionCalibrationColors);
      newCalibrationColors[index].name = "";
      newCalibrationColors[index].hex = "";
      setProductionCalibrationColors(newCalibrationColors);
      await saveCalibrationColors(newCalibrationColors);
    },
    [
      productionCalibrationColors,
      setProductionCalibrationColors,
      saveCalibrationColors,
    ]
  );

  return (
    <FlexColBox gap={1.5} maxWidth="240px" width="240px">
      <FlexColBox gap={1}>
        <Typography variant="textRegularBold">Position</Typography>
        <ListItem
          value={`${t(`positions.${decoration.location.selectedLocation.positionName}`)} (${decoration.location.selectedLocation.positionId})`}
        />
      </FlexColBox>
      {
        // The way we display the size and spacing info is different depending
        // on if the decoration is a tag/patch, a repeating pattern or anything
        // else. The code below displays the proper info based on the decoration
        // type.
      }
      <FlexColBox gap={1}>
        {decorationDefinition?.behavesLike === BEHAVES_LIKE.WOVEN_TAG_TYPE ||
        decorationDefinition?.behavesLike === BEHAVES_LIKE.LEATHER_TAG_TYPE ||
        decorationDefinition?.behavesLike ===
          BEHAVES_LIKE.EMBROIDERY_PATCH_TYPE ? (
          <>
            <FlexColBox gap={1}>
              {decorationDefinition?.behavesLike ===
              BEHAVES_LIKE.EMBROIDERY_PATCH_TYPE ? (
                <Typography variant="textRegularBold">Patch</Typography>
              ) : (
                <Typography variant="textRegularBold">Tag</Typography>
              )}
              <FlexColBox gap={0.5}>
                <ListItem
                  value={`W: ${tagDetails?.tagWidth}`}
                  withCopyButton
                  copyValue={formatNumber(tagDetails?.tagWidth)}
                />
                <ListItem
                  value={`H: ${tagDetails?.tagHeight}`}
                  withCopyButton
                  copyValue={formatNumber(tagDetails?.tagHeight)}
                />
              </FlexColBox>
            </FlexColBox>
            <FlexColBox gap={1}>
              <Typography variant="textRegularBold">Logo</Typography>
              <FlexColBox gap={0.5}>
                <ListItem
                  value={`W: ${tagDetails?.assetWidth}`}
                  withCopyButton
                  copyValue={formatNumber(tagDetails?.assetWidth)}
                />
                <ListItem
                  value={`H: ${tagDetails?.assetHeight}`}
                  withCopyButton
                  copyValue={formatNumber(tagDetails?.assetHeight)}
                />
                {decoration.area !== undefined && (
                  <ListItem
                    value={`A: ${decoration.area} in²`}
                    withCopyButton
                    copyValue={formatNumber(decoration.area)}
                  />
                )}
                {
                  // Depending on the decoration type, display the proper information
                  // regarding the colors/textures.
                  // Leather tag: Display the texture & color
                }

                {decorationDefinition?.behavesLike ===
                  BEHAVES_LIKE.LEATHER_TAG_TYPE && (
                  <ListItem
                    value={`Texture: ${t(`textures.${decoration.tagDetails.textureName}`)}`}
                    withCopyButton
                    copyValue={t(
                      `textures.${decoration.tagDetails.textureName}`
                    )}
                  />
                )}
              </FlexColBox>
            </FlexColBox>
          </>
        ) : // If there is a repeating pattern, display the details
        decorationDefinition?.repeatingPatternOptions ? (
          <React.Fragment>
            <FlexColBox gap={1}>
              <Typography variant="textRegularBold">
                {t(`specs.pattern_name`)}
              </Typography>
              <FlexColBox gap={0.5}>
                <ListItem
                  value={t(
                    `specs.repeating_patterns.${decoration.repeatingDetails.patternName}`
                  )}
                />
              </FlexColBox>
            </FlexColBox>

            <FlexColBox gap={1}>
              <Typography variant="textRegularBold">Logo</Typography>
              <FlexColBox gap={0.5}>
                <ListItem
                  value={`W: ${inchesToCm(repeatingPatternLogoSize?.width || 0)}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(repeatingPatternLogoSize?.width || 0)
                  )}
                />
                <ListItem
                  value={`H: ${inchesToCm(repeatingPatternLogoSize?.height || 0)}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(repeatingPatternLogoSize?.height || 0)
                  )}
                />
                {decoration.area !== undefined && (
                  <ListItem
                    value={`A: ${decoration.area} in²`}
                    withCopyButton
                    copyValue={formatNumber(decoration.area)}
                  />
                )}
              </FlexColBox>
            </FlexColBox>

            <FlexColBox gap={1}>
              <Typography variant="textRegularBold">Spacing</Typography>
              <FlexColBox gap={0.5}>
                <ListItem
                  value={`Vertical : ${inchesToCm(
                    decoration.repeatingDetails.verticalSpacingInches
                  )}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(
                      decoration.repeatingDetails.verticalSpacingInches
                    )
                  )}
                />
                <ListItem
                  value={`Horizontal : ${inchesToCm(
                    decoration.repeatingDetails.horizontalSpacingInches
                  )}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(
                      decoration.repeatingDetails.horizontalSpacingInches
                    )
                  )}
                />
              </FlexColBox>
            </FlexColBox>

            <FlexColBox gap={1}>
              <Typography variant="textRegularBold">Offset</Typography>
              <FlexColBox gap={0.5}>
                <ListItem
                  value={`Vertical : ${inchesToCm(
                    decoration.repeatingDetails.verticalOffsetInches
                  )}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(decoration.repeatingDetails.verticalOffsetInches)
                  )}
                />
                <ListItem
                  value={`Horizontal : ${inchesToCm(
                    decoration.repeatingDetails.horizontalOffsetInches
                  )}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(decoration.repeatingDetails.verticalOffsetInches)
                  )}
                />
              </FlexColBox>
            </FlexColBox>

            {patternBackgroundColorDef && (
              <FlexColBox gap={1}>
                <Typography variant="textRegularBold">
                  {t("techpack.background_color")}
                </Typography>
                <ArtworkCustomColor
                  chartName={patternBackgroundColorDef.chartName || "UW chart"}
                  label={
                    patternBackgroundColorDef.chartName === "Custom"
                      ? patternBackgroundColorDef.label
                      : t(`colors.${patternBackgroundColorDef.label}`)
                  }
                  techpackName={patternBackgroundColorDef.techpackColorName}
                  colorHex={patternBackgroundColorDef.hex}
                />
              </FlexColBox>
            )}
          </React.Fragment>
        ) : (
          decoration?.assetSize?.selectedSize && (
            <FlexColBox gap={1}>
              <Typography variant="textRegularBold">Logo</Typography>
              <FlexColBox gap={0.5}>
                <ListItem
                  value={`W: ${inchesToCm(decoration.assetSize.selectedSize.widthInInches)}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(decoration.assetSize.selectedSize.widthInInches)
                  )}
                />
                <ListItem
                  value={`H: ${inchesToCm(decoration.assetSize.selectedSize.heightInInches)}`}
                  withCopyButton
                  copyValue={formatNumber(
                    inchesToCm(decoration.assetSize.selectedSize.heightInInches)
                  )}
                />
                {decoration.area !== undefined && (
                  <ListItem
                    value={`A: ${decoration.area} in²`}
                    withCopyButton
                    copyValue={formatNumber(decoration.area)}
                  />
                )}
              </FlexColBox>
            </FlexColBox>
          )
        )}
      </FlexColBox>

      {
        // Any decoration that have the field fabricColorCodes set, we display
        // the colors used by the asset.
      }
      {decoration.fabricColorCodes && (
        <FlexColBox gap={1}>
          <Typography variant="textRegularBold">
            {t("techpack.logo_colors")}
          </Typography>

          {decoration.asset?.selectedColors
            ?.filter(
              (selectedColor) => selectedColor.transparent === false // Remove transparent color from the list
            )
            .filter((selectedColor, pos) => {
              // Remove duplicated colors
              const selectedColorNames = decoration.asset?.selectedColors?.map(
                (c) => c?.chartColor?.name
              );
              const currentColorName = selectedColor?.chartColor?.name;
              return selectedColorNames?.indexOf(currentColorName) === pos;
            })
            .map((selectedColor, selectedColorIndex) => {
              const isCustom = selectedColor.chartColor.name.startsWith("#");
              const fabricColor = decoration.fabricColorCodes?.find(
                (colorCode) => colorCode.name === selectedColor.chartColor.name
              );
              const colorHex = isCustom
                ? selectedColor.chartColor.name
                : fabricColor?.hex;
              const chartName = isCustom
                ? "Custom"
                : fabricColor?.chartName || "UW chart";
              const label = isCustom
                ? colorHex
                : t("colors." + fabricColor?.label);
              const techpackName = isCustom
                ? ""
                : selectedColor.chartColor?.techpackColorName ||
                  selectedColor.chartColor.name;

              return (
                <ArtworkCustomColor
                  key={selectedColorIndex}
                  chartName={chartName}
                  label={label}
                  techpackName={techpackName}
                  colorHex={colorHex}
                />
              );
            })}
        </FlexColBox>
      )}
      {
        // If a background exists for the tag, display it
      }
      {(decorationDefinition?.behavesLike === BEHAVES_LIKE.WOVEN_TAG_TYPE ||
        decorationDefinition?.behavesLike === BEHAVES_LIKE.LEATHER_TAG_TYPE ||
        decorationDefinition?.behavesLike ===
          BEHAVES_LIKE.EMBROIDERY_PATCH_TYPE) &&
        backgroundColorDef && (
          <FlexColBox gap={1}>
            <Typography variant="textRegularBold">
              {t("techpack.background_color")}
            </Typography>
            <ArtworkCustomColor
              chartName={backgroundColorDef.chartName || "UW chart"}
              label={t(`colors.${backgroundColorDef.label}`)}
              techpackName={backgroundColorDef.techpackColorName}
              colorHex={backgroundColorDef.hex}
            />
          </FlexColBox>
        )}
      {
        // If stitches exists for the tag, display it
      }
      {decorationDefinition?.behavesLike ===
        BEHAVES_LIKE.EMBROIDERY_PATCH_TYPE &&
        stitchesColorDef && (
          <FlexColBox gap={1}>
            <Typography variant="textRegularBold">
              {t("techpack.stitches_color")}
            </Typography>
            <ArtworkCustomColor
              chartName={stitchesColorDef.chartName || "UW chart"}
              label={t(`colors.${stitchesColorDef.label}`)}
              techpackName={stitchesColorDef.techpackColorName}
              colorHex={stitchesColorDef.hex}
            />
          </FlexColBox>
        )}

      {decorationDefinition?.behavesLike ===
        BEHAVES_LIKE.DETAILED_PRINT_TYPE && (
        <FlexColBox gap={1}>
          <Typography variant="textRegularBold">
            {t("techpack.calibration")}
          </Typography>

          {/**For now we hardcode to 4 calibration colors */}

          <FlexColBox gap={1}>
            {productionCalibrationColors?.map((calibrationColor, index) => {
              return (
                // If we set the calibration color before a production artwork was set,
                // the backend returns an error 400 and the colors are not saved. For
                // now, disable the calibration color as long as the production artwork
                // is not set.
                <Box key={index}>
                  <Input
                    value={calibrationColor?.name || ""}
                    sx={{
                      padding: "8px",
                      "&:hover": {
                        boxShadow: "none",
                        borderColor: palette.grey.darkerGrey,
                        "& fieldset": {
                          boxShadow: "none",
                        },
                      },
                      "&.Mui-focused": {
                        borderColor: palette.grey.darkerGrey,
                        boxShadow: "none",
                      },
                    }}
                    inputProps={{
                      sx: {
                        ml: "8px",
                      },
                    }}
                    startAdornment={
                      <ColorBoxPicker
                        value={calibrationColor?.hex}
                        onChange={(color) => {
                          onCalibrationColorChange(index, "hex", color);
                        }}
                      />
                    }
                    endAdornment={
                      calibrationColor?.name || calibrationColor?.hex ? (
                        <Icon
                          icon={"close"}
                          size={22}
                          onClick={() => clearColorSelection(index)}
                          color={palette.grey.nearBlack}
                        />
                      ) : undefined
                    }
                    onChange={(e) => {
                      onCalibrationColorChange(index, "name", e.target.value);
                    }}
                  />
                </Box>
              );
            })}
          </FlexColBox>
        </FlexColBox>
      )}
    </FlexColBox>
  );
};

export default TechpackArtworkDescription;
